import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'
import type { FormsScheduleRecurrenceTypeEnum } from 'generated/resources/datatable.graphql'
import {
  type SurveyHeaderFormFragment,
  UpdateFormScheduleDocument,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { RecurrenceSelect } from 'modules/engagement_surveys/components/RecurrenceSelect'
import { StartTimeSelect } from 'modules/engagement_surveys/components/StartTimeSelect'

interface ScheduleSettingsForm {
  startDate?: string
  startTime?: string
  recurrenceType: FormsScheduleRecurrenceTypeEnum
  daysToAnswer?: number
  endsAt?: string
}

interface Props {
  form: SurveyHeaderFormFragment
  onClose: () => void
}

export function ScheduleSettingsDialog({ form, onClose }: Props) {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  const [updateFormSchedule] = useMutation(UpdateFormScheduleDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })

  const formik = useFormik<ScheduleSettingsForm>({
    initialValues: {
      startDate: form.schedule?.startDate || undefined,
      startTime: form.schedule?.startTime || undefined,
      recurrenceType: form.schedule?.recurrenceType || 'onetime',
      daysToAnswer: form.schedule?.daysToAnswer || undefined,
      endsAt: form.endsAt || undefined,
    },
    onSubmit: async (values) => {
      const result = await updateFormSchedule({
        variables: {
          formId: form.id,
          schedule: {
            id: form.schedule?.id ? Number(form.schedule.id) : undefined,
            recurrenceType: values.recurrenceType,
            startDate: values.startDate,
            startTime: values.startTime,
            daysToAnswer: values.daysToAnswer,
          },
          endsAt: values.endsAt,
        },
      })

      const handled = handleResult(result, () => null)

      if (handled.success) {
        onClose()
      }
    },
  })

  return (
    <Modal
      title={i18n.t('surveys.edit_schedule_dialog.title')}
      illustration="surveys"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            type="primary"
            loading={formik.isSubmitting}
          >
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        {form.status === 'scheduled' && (
          <Box gap="s32">
            <Box flexDirection="row" alignItems="center" gap="s32" width="full">
              <DayPicker
                withNull
                label={i18n.t('forms.schedule.start_date')}
                value={formik.values.startDate}
                onChange={(startDate) => {
                  formik.setFieldValue('startDate', startDate)
                }}
                error={formik.errors.startDate}
                minDate={new Date()}
                firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                valueFormat="backend"
              />

              <StartTimeSelect
                value={formik.values.startTime}
                onChange={(startTime) => {
                  formik.setFieldValue('startTime', startTime)
                }}
                date={formik.values.startDate}
              />
            </Box>
          </Box>
        )}

        <Box gap="s16">
          <RecurrenceSelect
            value={formik.values.recurrenceType}
            onChange={(recurrenceType) => {
              formik.setFieldValue('recurrenceType', recurrenceType)
            }}
          />

          <Box flexDirection="row" alignItems="flexStart" gap="s32" width="full">
            <Box gap="s8" width="full">
              <DayPicker
                label={i18n.t('forms.schedule.end_date')}
                value={formik.values.endsAt}
                onChange={(endsAt) => {
                  formik.setFieldValue('endsAt', endsAt)
                }}
                error={formik.errors.endsAt}
                minDate={formik.values.startDate ? new Date(formik.values.startDate) : new Date()}
                firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                valueFormat="backend_datetime"
              />
              <Text.Normal size="200" color="grey700">
                {i18n.t('forms.schedule.end_date_description')}
              </Text.Normal>
            </Box>

            {formik.values.recurrenceType !== 'onetime' &&
            formik.values.recurrenceType !== 'daily' ? (
              <TextField
                label={i18n.t('forms.schedule.days_to_answer')}
                value={
                  formik.values.daysToAnswer && isFinite(formik.values.daysToAnswer)
                    ? formik.values.daysToAnswer?.toString()
                    : ''
                }
                onChange={(value) => {
                  const daysToAnswer = value && isFinite(Number(value)) ? Number(value) : undefined

                  formik.setFieldValue('daysToAnswer', daysToAnswer)
                }}
                error={formik.errors.daysToAnswer}
              />
            ) : (
              <Box width="full" />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
